import { useCallback, useState } from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import {
  replaceFile as replaceFileApi,
  uploadBatch,
  uploadComplete,
} from "../api/files.api";
import { useReplaceFile } from "./useReplaceFile";
import { CLIENT_UPLOAD_CONCURRENCY_MAX } from "../constants/limits";
import type { UploadBatchItem } from "../types/project-storage.types";
import type { UploadQueueItem, UploadQueueItemStatus } from "../types/upload-queue.types";
import { folderChildrenQueryKey } from "./useFolderChildren";
import { folderTreeQueryKey } from "./useFolderTree";
import { putToS3 } from "../utils/putToS3";
import { runConcurrent } from "../utils/runConcurrent";
import {
  getStorageErrorMessage,
  mapStorageError,
} from "../utils/storageErrors";
import {
  resolveUploadMimeType,
  validateFileClient,
  validateFileSelection,
} from "../utils/validateFileClient";
import { chunkArray, isReadyForS3Put } from "../utils/uploadFlow";

function createQueueItemId(file: File): string {
  return `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`;
}

function createQueueItem(file: File): UploadQueueItem {
  const validation = validateFileClient(file);
  const mimeType = resolveUploadMimeType(file);

  if (!validation.ok) {
    return {
      id: createQueueItemId(file),
      file,
      displayName: file.name,
      mimeType,
      sizeBytes: file.size,
      status: "failed",
      errorCode: validation.errorCode,
      errorMessage: validation.message,
    };
  }

  return {
    id: createQueueItemId(file),
    file,
    displayName: file.name,
    mimeType,
    sizeBytes: file.size,
    status: "pending",
    warning: validation.warning,
  };
}

function mapBatchItemStatus(item: UploadBatchItem): UploadQueueItemStatus {
  switch (item.status) {
    case "ready_to_upload":
      return "uploading";
    case "name_conflict":
      return "name_conflict";
    default:
      return "failed";
  }
}

function batchItemToQueueUpdate(
  batchItem: UploadBatchItem
): Partial<UploadQueueItem> {
  const status = mapBatchItemStatus(batchItem);

  if (status === "failed") {
    const errorCode =
      batchItem.errorCode ??
      (batchItem.status === "file_too_large"
        ? "FILE_TOO_LARGE"
        : batchItem.status === "invalid_extension"
          ? "INVALID_EXTENSION"
          : "INVALID_MIME");

    return {
      status,
      errorCode,
      errorMessage: getStorageErrorMessage(errorCode),
    };
  }

  if (status === "name_conflict") {
    return {
      status,
      errorCode: "NAME_CONFLICT",
      errorMessage: getStorageErrorMessage("NAME_CONFLICT"),
      existingFileId: batchItem.existingFileId,
    };
  }

  return {
    status: "uploading",
    fileId: batchItem.fileId ?? undefined,
  };
}

function canRetryUpload(item: UploadQueueItem): boolean {
  return (
    item.status === "failed" &&
    !!item.fileId &&
    (item.errorCode === "FILE_NOT_FOUND_IN_STORAGE" || item.errorCode === undefined)
  );
}

type ReadyUploadPair = {
  queueItem: UploadQueueItem;
  batchItem: UploadBatchItem;
};

export function useUploadBatch(mmCode: number | null, folderId: string | null) {
  const queryClient = useQueryClient();
  const [queue, setQueue] = useState<UploadQueueItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [retryingItemId, setRetryingItemId] = useState<string | null>(null);
  const { replaceConflictFile, replacingItemId, isReplacing } = useReplaceFile(
    mmCode,
    folderId
  );

  const updateQueueItem = useCallback(
    (id: string, patch: Partial<UploadQueueItem>) => {
      setQueue((current) =>
        current.map((item) => (item.id === id ? { ...item, ...patch } : item))
      );
    },
    []
  );

  const invalidateFolderData = useCallback(async () => {
    if (mmCode === null || !folderId) return;

    await Promise.all([
      queryClient.invalidateQueries(folderChildrenQueryKey(mmCode, folderId)),
      queryClient.invalidateQueries(folderTreeQueryKey(mmCode)),
    ]);
  }, [folderId, mmCode, queryClient]);

  const putReadyItemsToS3 = useCallback(
    async (readyItems: ReadyUploadPair[]): Promise<string[]> => {
      const putSucceededIds: string[] = [];

      await runConcurrent(
        readyItems,
        CLIENT_UPLOAD_CONCURRENCY_MAX,
        async ({ queueItem, batchItem }) => {
          if (!isReadyForS3Put(batchItem) || !batchItem.fileId) return;

          updateQueueItem(queueItem.id, {
            status: "uploading",
            fileId: batchItem.fileId,
            errorMessage: undefined,
            errorCode: undefined,
          });

          const putOk = await putToS3(
            batchItem.presignedUrl!,
            queueItem.mimeType,
            queueItem.file
          );

          if (putOk) {
            putSucceededIds.push(batchItem.fileId);
            return;
          }

          updateQueueItem(queueItem.id, {
            status: "failed",
            fileId: batchItem.fileId,
            errorMessage: getStorageErrorMessage("FILE_NOT_FOUND_IN_STORAGE"),
          });
        }
      );

      return putSucceededIds;
    },
    [updateQueueItem]
  );

  const completeSuccessfulPuts = useCallback(
    async (
      readyItems: ReadyUploadPair[],
      putSucceededIds: string[]
    ): Promise<number> => {
      if (mmCode === null || putSucceededIds.length === 0) return 0;

      readyItems.forEach(({ queueItem, batchItem }) => {
        if (batchItem.fileId && putSucceededIds.includes(batchItem.fileId)) {
          updateQueueItem(queueItem.id, { status: "completing" });
        }
      });

      const completeResponse = await uploadComplete(mmCode, {
        fileIds: putSucceededIds,
      });

      const fileIdToQueueItem = new Map(
        readyItems
          .filter(({ batchItem }) => batchItem.fileId !== null)
          .map(({ queueItem, batchItem }) => [batchItem.fileId!, queueItem])
      );

      let successCount = 0;

      for (const result of completeResponse.results) {
        const queueItem = fileIdToQueueItem.get(result.fileId);
        if (!queueItem) continue;

        if (result.status === "uploaded") {
          updateQueueItem(queueItem.id, {
            status: "uploaded",
            fileId: result.fileId,
          });
          successCount += 1;
          continue;
        }

        const errorCode = result.errorCode ?? "FILE_NOT_FOUND_IN_STORAGE";
        updateQueueItem(queueItem.id, {
          status: "failed",
          fileId: result.fileId,
          errorCode,
          errorMessage: getStorageErrorMessage(errorCode),
        });
      }

      return successCount;
    },
    [mmCode, updateQueueItem]
  );

  const processUploadChunk = useCallback(
    async (validItems: UploadQueueItem[]): Promise<number> => {
      if (mmCode === null || !folderId) return 0;

      validItems.forEach((item) =>
        updateQueueItem(item.id, { status: "batching" })
      );

      const batchResponse = await uploadBatch(
        mmCode,
        folderId,
        validItems.map((item) => ({
          displayName: item.displayName,
          mimeType: item.mimeType,
          sizeBytes: item.sizeBytes,
        }))
      );

      const readyItems: ReadyUploadPair[] = [];

      batchResponse.items.forEach((batchItem, index) => {
        const queueItem = validItems[index];
        if (!queueItem) return;

        updateQueueItem(queueItem.id, batchItemToQueueUpdate(batchItem));

        if (isReadyForS3Put(batchItem)) {
          readyItems.push({ queueItem, batchItem });
        }
      });

      const putSucceededIds = await putReadyItemsToS3(readyItems);
      return completeSuccessfulPuts(readyItems, putSucceededIds);
    },
    [
      completeSuccessfulPuts,
      folderId,
      mmCode,
      putReadyItemsToS3,
      updateQueueItem,
    ]
  );

  const retryUploadWithReplace = useCallback(
    async (item: UploadQueueItem, fileId: string): Promise<boolean> => {
      if (mmCode === null) return false;

      updateQueueItem(item.id, {
        status: "uploading",
        errorMessage: undefined,
        errorCode: undefined,
      });

      try {
        const replaceInit = await replaceFileApi(mmCode, fileId, {
          displayName: item.displayName,
          mimeType: item.mimeType,
          sizeBytes: item.sizeBytes,
        });

        const putOk = await putToS3(
          replaceInit.presignedUrl,
          item.mimeType,
          item.file
        );

        if (!putOk) {
          updateQueueItem(item.id, {
            status: "failed",
            fileId,
            errorMessage: getStorageErrorMessage("FILE_NOT_FOUND_IN_STORAGE"),
          });
          return false;
        }

        const completeResponse = await uploadComplete(mmCode, {
          fileIds: [fileId],
        });

        const completeResult = completeResponse.results.find(
          (result) => result.fileId === fileId
        );

        if (completeResult?.status === "uploaded") {
          updateQueueItem(item.id, { status: "uploaded", fileId });
          return true;
        }

        const errorCode =
          completeResult?.errorCode ?? "FILE_NOT_FOUND_IN_STORAGE";
        updateQueueItem(item.id, {
          status: "failed",
          fileId,
          errorCode,
          errorMessage: getStorageErrorMessage(errorCode),
        });
        return false;
      } catch (error) {
        const mapped = mapStorageError(error, "upload");
        updateQueueItem(item.id, {
          status: "failed",
          fileId,
          errorCode: mapped.errorCode,
          errorMessage: mapped.message,
        });
        return false;
      }
    },
    [mmCode, updateQueueItem]
  );

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (mmCode === null || !folderId) {
        toast.error("Selecione uma pasta para enviar arquivos.");
        return;
      }

      const selection = validateFileSelection(files);
      const newItems = selection.files.map(createQueueItem);
      setQueue((current) => [...newItems, ...current]);

      const validItems = newItems.filter((item) => item.status === "pending");
      if (validItems.length === 0) return;

      setIsUploading(true);

      try {
        const chunks = chunkArray(validItems);
        let totalSuccessCount = 0;

        for (const chunk of chunks) {
          totalSuccessCount += await processUploadChunk(chunk);
        }

        if (totalSuccessCount > 0) {
          await invalidateFolderData();

          toast.success(
            totalSuccessCount === 1
              ? "Arquivo enviado com sucesso."
              : `${totalSuccessCount} arquivos enviados com sucesso.`
          );
        }
      } catch (error) {
        const mapped = mapStorageError(
          error,
          "upload",
          "Não foi possível iniciar o envio dos arquivos."
        );

        toast.error(mapped.message);

        validItems.forEach((item) =>
          updateQueueItem(item.id, {
            status: "failed",
            errorCode: mapped.errorCode,
            errorMessage: mapped.message,
          })
        );
      } finally {
        setIsUploading(false);
      }
    },
    [
      folderId,
      invalidateFolderData,
      mmCode,
      processUploadChunk,
      updateQueueItem,
    ]
  );

  const replaceConflictItem = useCallback(
    async (item: UploadQueueItem) => {
      if (item.status !== "name_conflict" || !item.existingFileId) return;

      updateQueueItem(item.id, {
        status: "uploading",
        errorMessage: undefined,
        errorCode: undefined,
      });

      const result = await replaceConflictFile(item);

      if (result.ok) {
        updateQueueItem(item.id, {
          status: "uploaded",
          fileId: item.existingFileId,
        });
        toast.success("Arquivo substituído com sucesso.");
        return;
      }

      updateQueueItem(item.id, {
        status: "failed",
        errorCode: result.errorCode,
        errorMessage: result.errorMessage,
      });
      toast.error(result.errorMessage);
    },
    [replaceConflictFile, updateQueueItem]
  );

  const retryUploadItem = useCallback(
    async (item: UploadQueueItem) => {
      if (!canRetryUpload(item) || !item.fileId) return;

      setRetryingItemId(item.id);

      try {
        const uploaded = await retryUploadWithReplace(item, item.fileId);
        if (uploaded) {
          await invalidateFolderData();
          toast.success("Arquivo enviado com sucesso.");
        }
      } finally {
        setRetryingItemId(null);
      }
    },
    [invalidateFolderData, retryUploadWithReplace]
  );

  const clearFinishedItems = useCallback(() => {
    setQueue((current) =>
      current.filter(
        (item) =>
          item.status !== "uploaded" &&
          item.status !== "failed" &&
          item.status !== "name_conflict"
      )
    );
  }, []);

  return {
    queue,
    isUploading: isUploading || isReplacing || retryingItemId !== null,
    replacingItemId,
    retryingItemId,
    uploadFiles,
    replaceConflictItem,
    retryUploadItem,
    clearFinishedItems,
    canRetryUpload,
  };
}
